// src/utils/formatNumbers.js
export const DECIMALS = {
  TOKEN_AMOUNT: 6,
  EUR_PRICE: 8,
  EUR_ROW: 5,
  EUR_SUMMARY: 2,
};

export function isFiniteNumber(v) {
  const n = Number(v);
  return Number.isFinite(n);
}

export function formatUnitsFromRaw(raw, decimals = 18) {
  try {
    const d = Number.isFinite(Number(decimals)) ? Number(decimals) : 18;
    const bi = BigInt(String(raw).trim());
    const base = 10n ** BigInt(d);
    const i = bi / base;
    const f = bi % base;

    if (d === 0) {
      return i.toString();
    }

    const frac = f.toString().padStart(d, '0').replace(/0+$/, '');
    return frac ? `${i.toString()}.${frac}` : i.toString();
  } catch {
    return '';
  }
}

function isBlankAmountValue(value) {
  if (value === null || value === undefined) {
    return true;
  }

  const s = String(value).trim();
  return !s || s === '—' || s === '-';
}

/**
 * Resolve a token amount as a decimal string for display.
 * Falls back to amount_raw when display fields are empty.
 */
export function resolveTokenAmountString(tx = {}, defaultDecimals = 18) {
  const candidates = [
    tx.amount_display,
    tx.amountDisplay,
    tx.amount,
    tx.value,
  ];

  for (const candidate of candidates) {
    if (isBlankAmountValue(candidate)) {
      continue;
    }

    const asString = String(candidate).trim();
    const asNumber = Number(asString);

    if (Number.isFinite(asNumber)) {
      return asString;
    }
  }

  const raw = tx.amount_raw ?? tx.amountRaw;
  if (!isBlankAmountValue(raw)) {
    const decimals = Number(
      tx.token_decimals ??
        tx.tokenDecimals ??
        tx.decimals ??
        defaultDecimals
    );
    const fromRaw = formatUnitsFromRaw(raw, decimals);
    if (fromRaw) {
      return fromRaw;
    }
  }

  return '0';
}

/**
 * Format a transfer amount for tables.
 * Never returns an em dash for valid on-chain transfers with raw units.
 */
export function formatTxTokenAmount(
  tx = {},
  displayDecimals = DECIMALS.TOKEN_AMOUNT,
  defaultTokenDecimals = 18
) {
  const value = resolveTokenAmountString(tx, defaultTokenDecimals);
  const formatted = fmtTokenAmountFixed(value, displayDecimals);

  if (formatted === '—') {
    return fmtTokenAmountFixed('0', displayDecimals);
  }

  return formatted;
}

/**
 * Full-precision amount string for tooltips on dust-sized transfers.
 */
export function resolveTokenAmountTooltip(tx = {}, defaultDecimals = 18) {
  const full = resolveTokenAmountString(tx, defaultDecimals);
  if (full === '0') {
    return '';
  }

  return `${full} ${String(tx.token || tx.token_symbol || '').trim()}`.trim();
}

export function fmtFixed(v, d) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : "—";
}

export function trimZeros(s) {
  // "123.45000000" -> "123.45", "10.00000000" -> "10"
  return String(s).replace(/\.?0+$/, "");
}

// Rounds a decimal string to `places` decimals (half-up) and trims trailing zeros.
// Useful for values coming in as strings (e.g. amount_display) where Number(...) would lose precision.
export function roundDecimalString(s, places = DECIMALS.TOKEN_AMOUNT) {
  const str = String(s || "").trim();
  if (!str) return "—";

  const neg = str.startsWith("-");
  const abs = neg ? str.slice(1) : str;
  if (!abs.includes(".")) return neg ? `-${abs}` : abs;

  const [intRaw, fracRaw = ""] = abs.split(".");
  const intPart = (intRaw || "0").replace(/^0+(?=\\d)/, "") || "0";

  const placesN = Number(places);
  if (!Number.isFinite(placesN) || placesN <= 0) {
    const frac0 = (fracRaw || "").padEnd(1, "0");
    const shouldUp = frac0[0] >= "5";
    if (!shouldUp) return neg ? `-${intPart}` : intPart;

    let carry = 1;
    const digs = intPart.split("");
    for (let i = digs.length - 1; i >= 0; i--) {
      const n = (digs[i].charCodeAt(0) - 48) + carry;
      digs[i] = String(n % 10);
      carry = n >= 10 ? 1 : 0;
      if (!carry) break;
    }
    if (carry) digs.unshift("1");
    const out = digs.join("");
    return neg ? `-${out}` : out;
  }

  const frac = String(fracRaw || "").padEnd(placesN + 1, "0");
  const keep = frac.slice(0, placesN);
  const next = frac[placesN] || "0";

  let kept = keep;
  let intOut = intPart;

  if (next >= "5") {
    let carry = 1;
    const digs = kept.split("");
    for (let i = digs.length - 1; i >= 0; i--) {
      const n = (digs[i].charCodeAt(0) - 48) + carry;
      digs[i] = String(n % 10);
      carry = n >= 10 ? 1 : 0;
      if (!carry) break;
    }
    kept = digs.join("");

    if (carry) {
      let carryInt = 1;
      const id = intOut.split("");
      for (let i = id.length - 1; i >= 0; i--) {
        const n = (id[i].charCodeAt(0) - 48) + carryInt;
        id[i] = String(n % 10);
        carryInt = n >= 10 ? 1 : 0;
        if (!carryInt) break;
      }
      if (carryInt) id.unshift("1");
      intOut = id.join("");
      kept = "0".repeat(placesN);
    }
  }

  const out = kept ? `${intOut}.${trimZeros(kept)}` : intOut;
  const normalized = out.endsWith(".") ? out.slice(0, -1) : out;
  const signed = neg && normalized !== "0" ? `-${normalized}` : normalized;
  return trimZeros(signed);
}

// NEW: pad to exactly `places` decimals (no trimming)
export function padDecimalStringFixed(s, places = DECIMALS.TOKEN_AMOUNT) {
  const str = String(s ?? "").trim();
  if (!str || str === "—") return "—";

  const neg = str.startsWith("-");
  const abs = neg ? str.slice(1) : str;

  const [iRaw, fRaw = ""] = abs.split(".");
  const i = (iRaw || "0").replace(/^0+(?=\\d)/, "") || "0";
  const placesN = Number(places);

  if (!Number.isFinite(placesN) || placesN < 0) return (neg ? "-" : "") + abs;

  const f = String(fRaw).padEnd(placesN, "0").slice(0, placesN);
  const out = placesN === 0 ? i : `${i}.${f}`;
  return neg && out !== "0" ? `-${out}` : out;
}

// NEW: round string to `places` (existing function), then pad to fixed decimals
export function roundDecimalStringFixed(s, places = DECIMALS.TOKEN_AMOUNT) {
  const rounded = roundDecimalString(s, places); // may trim
  return padDecimalStringFixed(rounded, places);
}

// NEW: fixed token amount formatter (always shows exactly d decimals)
export function fmtTokenAmountFixed(v, d = DECIMALS.TOKEN_AMOUNT) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return roundDecimalStringFixed(v, d);
  if (!isFiniteNumber(v)) return "—";
  // toFixed pads zeros by definition
  return fmtFixed(v, d);
}

// Token quantities (8 dp, trim)
export function fmtTokenAmount(v, d = DECIMALS.TOKEN_AMOUNT) {
  if (!isFiniteNumber(v)) return "—";
  return trimZeros(fmtFixed(v, d));
}

// EUR unit price (8 dp, keep fixed for readability)
export function fmtEurPrice(v, d = DECIMALS.EUR_PRICE) {
  if (!isFiniteNumber(v)) return "—";
  return fmtFixed(v, d);
}

// EUR money in rows (5 dp)
export function fmtEurRow(v, d = DECIMALS.EUR_ROW) {
  if (!isFiniteNumber(v)) return "—";
  return fmtFixed(v, d);
}

// EUR money in summaries/footers (2 dp)
export function fmtEurSummary(v, d = DECIMALS.EUR_SUMMARY) {
  if (!isFiniteNumber(v)) return "—";
  return fmtFixed(v, d);
}

